import React, { useContext, Fragment, useMemo } from 'react';
import { object } from 'prop-types';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';

import { ImagePlaceholder } from 'components/ImagePlaceholder';
import { ImageTechDoc } from 'components/ImageTechDoc';
import { DealerLocator } from 'components/DealerLocator';
import ReferenceReplacement from './components/ReferenceReplacement';

import { VALEO_BRANDS, getIsValeoBrand } from 'utils/constantsHelper';
import browserHistory from 'utils/browserHistory';
import { concatCharacteristics } from 'utils/concatCharacteristics';
import { getCurrentTab } from 'utils/tabsHelper';
import { baiduTriggerEvent } from 'utils/baiduTriggerEvent';
import { gtmPushEvent } from 'utils/gtmPushEvent';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';
import { CountryContext } from 'context/CountryContext';
import { ProductContext } from 'context/ProductContext';

import { withErrorBoundary } from 'containers/ErrorBoundary';

import ArrowRightIcon from 'media/icons/arrow-right-filled.icon.svg';
import PlanetLogo from 'media/planet-logo.png';
import brands from './brands-info.json';

import styles from './SearchItem.module.scss';
const cx = classNames.bind(styles);

const SearchItem = ({ data }) => {
  const { labels } = useContext(StaticTextContext);
  const { currentCountry, countryHash } = useContext(CountryContext);
  const { updateProductState } = useContext(ProductContext);
  const { manuId, modelId, vehicleType, carId } = useContext(SearchContext);
  const { brandNo } = data.assignedArticle;

  const getLinkToProduct = (path) => {
    let currentTab = getCurrentTab();
    const { articleId, articleLinkId } = data.assignedArticle;

    if (manuId && modelId && articleLinkId) {
      return {
        pathname: `/product/${articleId}/${path}`,
        search: `?modId=${modelId}&manuId=${manuId}&linkingTargetId=${carId}&articleLinkId=${articleLinkId}&search=${currentTab}&vehicleType=${vehicleType}${
          !!countryHash ? '&country=' + countryHash : ''
        }`,
      };
    } else {
      return {
        pathname: `/product/${articleId}/${path}`,
        search: `?search=${currentTab}${
          !!countryHash ? '&country=' + countryHash : ''
        }`,
      };
    }
  };

  const linkToProduct = getLinkToProduct('about');
  const saveProduct = (event) => {
    event.preventDefault();
    // Trigger Baidu
    baiduTriggerEvent({ event: 'searchTAResultClick', payload: { ...data.assignedArticle, currentCountry } });

    updateProductState(data);
    browserHistory.push(linkToProduct);
  };
  const isValeoProduct = useMemo(() => getIsValeoBrand(VALEO_BRANDS, brandNo), [
    VALEO_BRANDS,
    brandNo,
  ]);
  const isRemanProduct = useMemo(() => {
    return !!(
      data.articleAttributes &&
      data.articleAttributes.array &&
      data.articleAttributes.array.some((attr) => attr.attrId === 591)
    );
  }, [data]);
  const brandLogoElem = brands.data.array.filter(
    (item) => item.brandId === brandNo
  )[0];
  const brandLogoId = brandLogoElem ? brandLogoElem.brandLogoID : null;
  const attributes = data.articleAttributes
    ? concatCharacteristics(data.articleAttributes.array)
    : [];

  const replacedByNumber = data.replacedByNumber.array;
  const replacedNumber = data.replacedNumber.array;

  const handleReferenceClick = (referenceNumber) => {
    const currentTab = getCurrentTab();
    const eventNameMap = {
      reference: 'clickReferringProductByRef',
      car: 'clickReferringProductByModelCar',
      vin: 'clickReferringProductByVIN',
      kba: 'clickReferringProductByKBA'
    }

    const {
      assignedArticle: {
        articleName,
        articleAddName,
        articleStateName,
        articleNo,
      },
    } = data;

    gtmPushEvent({
      event: eventNameMap[currentTab],
      productName: `${articleName} ${articleAddName}`,
      productReference: `${referenceNumber}`,
      productStatus: articleStateName,
      parentProductReference: articleNo,
    });
  }

  return (
    <article className={cx('product', { 'valeo-product': isValeoProduct })}>
      <div className={cx('images')}>
        {brandLogoId ? (
          <div className={cx('brand-img')}>
            <ImageTechDoc
              id={brandLogoId}
              alt={data.assignedArticle.brandName}
            />
          </div>
        ) : (
          <div className={cx('brand-title')}>
            {data.assignedArticle.brandName}
          </div>
        )}

        <div className={cx('product-img')}>
          {data.articleThumbnails && data.articleThumbnails.array ? (
            <ImageTechDoc
              id={data.articleThumbnails.array[0].thumbDocId}
              alt={data.assignedArticle.articleName}
            />
          ) : (
            <ImagePlaceholder className={cx('placeholder-icon')} />
          )}
        </div>
      </div>
      <section className={cx('details')}>
        <div className={cx('container')}>
          <div>
            <div className={cx('indicator')}>
              {data.assignedArticle.articleState === 1 ? (
                <span className={cx('text')}>
                  {labels.productStatusAvailableLabel}
                </span>
              ) : (
                <span className={cx('text')}>
                  {data.assignedArticle.articleStateName}
                </span>
              )}
            </div>
            <h2 className={cx('title')}>
              {data.assignedArticle.articleName}{' '}
              {data.assignedArticle.articleAddName}
            </h2>
            <div className={cx('ref-number')}>
          {labels.searchItemRefNumberLabel} : {data.assignedArticle.articleNo}
            </div>
          </div>
          {isRemanProduct && (
            <a target="_balnk" href="https://www.valeoservice.com/en-com/i-care-planet" className={cx('planet-logo-container')}>
              <img src={PlanetLogo} alt="" className={cx('planet-logo')} />
            </a>
          )}
        </div>
        <div className={cx('description')}>
          <div className={cx('attributes')}>
            {attributes.map((item) => (
              <Fragment key={item.attrValueId}>
                {item.attrValue && item.attrValue.length && (
                  <p>
                    <strong>{item.attrName}:</strong>
                    {' ' + item.attrValue}
                    &nbsp;
                    {item.attrUnit || ''}
                  </p>
                )}
              </Fragment>
            ))}
          </div>
          {replacedByNumber && (
            <ReferenceReplacement
              title={labels.replacedLabel}
              references={replacedByNumber}
              onClick={handleReferenceClick}
              parentReference={data.assignedArticle.articleNo}
              seeMoreLink={getLinkToProduct('replaced-by')}
            />
          )}
          {replacedNumber && (
            <ReferenceReplacement
              title={labels.replacesLabel}
              references={replacedNumber}
              onClick={handleReferenceClick}
              parentReference={data.assignedArticle.articleNo}
              seeMoreLink={getLinkToProduct('replaces')}
            />
          )}
        </div>
        <div className={cx('btns-container')}>
          <DealerLocator />
          <Link to={linkToProduct} onClick={saveProduct} className={cx('btn')}>
          <span className={cx('btn-text')}>{labels.searchItemButtonText}</span>
            <span className={cx('btn-icon')}>
              <ArrowRightIcon />
            </span>
          </Link>
        </div>
      </section>
    </article>
  );
};

SearchItem.propTypes = {
  data: object.isRequired,
};

export default withErrorBoundary(SearchItem);



// WEBPACK FOOTER //
// ./src/components/SearchItem/SearchItem.js